import { Controller } from "@hotwired/stimulus"

// Mirrors Cc::Tabs' active/inactive class sets (see app/components/cc/tabs.rb)
// so the client-side card/ACH toggle matches the server-rendered tab styling.
const TAB_ACTIVE_CLASSES = ["bg-slate-700", "border-black", "text-white"]
const TAB_INACTIVE_CLASSES = [
  "bg-white",
  "border",
  "hover:border-slate-700",
  "hover:text-slate-500",
  "text-slate-400",
]

export default class extends Controller {
  static targets = [
    "ach",
    "achButton",
    "achContainer",
    "achNotice",
    "card",
    "cardButton",
    "cardContainer",
    "cardError",
    "name",
    "select",
    "token",
  ]
  static values = {
    publicKey: String,
    src: String,
    url: String,
  }

  computeStyles = () => {
    const fieldGap = 1.5
    const style = getComputedStyle(this.nameTarget)
    const inputStyles = {
      "background-color": style.getPropertyValue("background-color"),
      "border-radius": style.getPropertyValue("border-radius"),
      "font-family": style.getPropertyValue("font-family"),
      "font-size": style.getPropertyValue("font-size"),
      "font-weight": style.getPropertyValue("font-weight"),
      "line-height": style.getPropertyValue("line-height"),
      border: style.getPropertyValue("border"),
      color: style.getPropertyValue("color"),
      height: "auto",
      margin: style.getPropertyValue("margin"),
      padding: style.getPropertyValue("padding"),
      transition: "border-color 0.3s ease, box-shadow 0.3s ease",
    }

    const selectStyle = getComputedStyle(this.selectTarget)
    const selectStyles = {
      ...inputStyles,
      "-moz-appearance": "none",
      "-webkit-appearance": "none",
      appearance: "none",
      "background-image": selectStyle.getPropertyValue("background-image"),
      "background-position-x": "calc(100% - 8px)",
      "background-position-y": "50%",
      "background-repeat": selectStyle.getPropertyValue("background-repeat"),
      "background-size": selectStyle.getPropertyValue("background-size"),
      padding: "8px",
      "padding-right": "16px",
    }
    const styles = {
      ".card .cc input, input": inputStyles,
      ".ach .fieldsetrow, .card": {
        gap: `${fieldGap}rem`,
      },
      ".card > div:not(:last-child):not(.break)": {
        padding: "0",
      },
      ".ach .fieldsetrow .break, .break": {
        display: "none",
      },
      ".fieldset, .fieldsetrow": {
        padding: "0",
      },
      ".first_name label, .last_name label": {
        display: "none",
      },
      ".fieldset.first_name": {
        "padding-top": "8px !important",
      },
      "input:focus,select:focus": {
        "box-shadow": "0 0 0 0.175rem black inset",
        outline: "none",
      },
      ".billing label": {
        display: "none",
      },
      ".billing > div:last-child": {
        display: "none",
      },
      ".card .cc input": {
        "padding-left": "2.75rem",
      },
      ".card .cc .cc-icon": {
        left: "0.8rem",
        padding: "0",
        top: "0",
      },
      ".card .cvv": {
        "flex-basis": `calc(33.333% - ${fieldGap * 0.5}rem)`,
      },
      ".card .exp": {
        "flex-basis": `calc(66.667% - ${fieldGap * 0.5}rem)`,
      },
      select: selectStyles,
    }

    return styles
  }

  connect() {
    this.init().then(this.loadFrame)
    this.sanitizeName({ currentTarget: this.nameTarget })
    this.selectedType = "card"

    // ACH-only companies render the card container hidden (see
    // PaymentMethodForm), so start on the bank account fields instead.
    if (this.cardContainerTarget.classList.contains("hidden")) this.showAch()
  }

  ensureToken = (e) => {
    if (this.tokenTarget.value.length < 5) {
      e.preventDefault()
      window.confirmMethod(
        "Account invalid",
        e.currentTarget,
        `Please enter your ${this.selectedType === "ach" ? "bank account" : "card"} information`
      )
    }
  }

  init() {
    return new Promise((resolve, reject) => {
      if (window.Tokenizer) {
        return resolve()
      }

      const script = document.createElement("script")
      script.src = this.srcValue
      script.onerror = reject
      script.onload = () => {
        resolve()
      }
      document.head.appendChild(script)
    })
  }

  loadFrame = () => {
    this.styles = this.computeStyles()
    if (this.hasAchTarget) {
      this.ach = this.newToken("ach", this.achTarget)
    }
    this.card = this.newToken("card", this.cardTarget)
  }

  newToken = (type, container) => {
    return new Tokenizer({
      apikey: this.publicKeyValue,
      container,
      settings: {
        payment: {
          ach: {
            secCodeList: ["ppd"],
            sec_code: "ppd",
            showSecCode: false,
          },
          card: { requireCVV: true },
          types: [type],
        },
        styles: this.styles,
      },
      submission: this.tokenSubmission,
      url: this.urlValue,
    })
  }

  sanitizeName = (e) => {
    e.currentTarget.value = e.currentTarget.value.replace(/[^a-z -]/gi, "")
  }

  // The card/ACH selector renders through Cc::Tabs (see PaymentMethodForm), so
  // the toggle swaps that component's active/inactive class sets. Kept in sync
  // with cc/tabs.rb by hand — a divergence only changes the tab's appearance.
  setTabActive = (button) => {
    button.classList.remove(...TAB_ACTIVE_CLASSES, ...TAB_INACTIVE_CLASSES)
    button.classList.add(...TAB_ACTIVE_CLASSES)
  }

  setTabInactive = (button) => {
    button.classList.remove(...TAB_ACTIVE_CLASSES, ...TAB_INACTIVE_CLASSES)
    button.classList.add(...TAB_INACTIVE_CLASSES)
  }

  showAch = (e) => {
    this.selectedType = "ach"
    this.achContainerTarget.classList.remove("hidden")
    if (this.hasAchNoticeTarget) this.achNoticeTarget.classList.remove("hidden")
    this.cardContainerTarget.classList.add("hidden")

    if (this.hasAchButtonTarget) this.setTabActive(this.achButtonTarget)
    if (this.hasCardButtonTarget) this.setTabInactive(this.cardButtonTarget)

    this.element.querySelectorAll("[data-ach-required]").forEach((container) => {
      container.querySelectorAll("input, select").forEach((input) => {
        input.required = true
        if (input.dataset.prefillValue) {
          if (!input.value) input.value = input.dataset.prefillValue
          delete input.dataset.prefillValue
        }
      })
      container.classList.remove("hidden")
    })
  }

  showCard = (e) => {
    this.selectedType = "card"
    this.cardContainerTarget.classList.remove("hidden")
    this.achContainerTarget.classList.add("hidden")
    if (this.hasAchNoticeTarget) this.achNoticeTarget.classList.add("hidden")

    this.setTabActive(this.cardButtonTarget)
    if (this.hasAchButtonTarget) this.setTabInactive(this.achButtonTarget)

    this.element.querySelectorAll("[data-ach-required]").forEach((container) => {
      container.querySelectorAll("input, select").forEach((input) => {
        input.required = false
      })
      container.classList.add("hidden")
    })
  }

  submit = (e) => {
    // NOTE: removed since it seems like this was causing issues with form resubmission
    // after a validation error. Leaving it commented out for now.
    // if (this.tokenTarget.value.length > 5) {
    //   this.element.closest("form").requestSubmit()
    //   return
    // }

    e.currentTarget.disabled = true

    if (this.selectedType === "ach") {
      this.ach.submit()
    }
    if (this.selectedType === "card") {
      this.card.submit()
    }
  }

  submitAch = (e) => {
    this.submit(e)
  }
  submitCard = (e) => {
    this.submit(e)
  }

  tokenSubmission = (resp) => {
    setTimeout(() => {
      this.element.querySelectorAll("button").forEach((b) => (b.disabled = false))
    }, 4000)

    switch (resp.status) {
      case "success":
        this.tokenTarget.value = resp.token
        // Announce the token to any surrounding controller (e.g. the hosted
        // payment-method-token page displays it). Harmless where unheard.
        this.dispatch("token", { detail: { token: resp.token } })

        if (this.element.dataset.selfSignUp == "true") {
          this.dispatch("success", { prefix: "tokenized-status" })
        }

        if (this.element.dataset.submitOnToken == "true") {
          this.tokenTarget.closest("form").requestSubmit()
        }

        break
      case "error":
        console.log("Card Error", { resp })
        break
      case "validation":
        if (resp.invalid.includes("cc")) {
          this.cardErrorTarget.classList.remove("hidden")
        } else {
          this.cardErrorTarget.classList.add("hidden")
        }
        break
      default:
        console.log("Error", { resp })
    }
  }
}
